/**
 * TableSeatRing — SVG overlay that draws stylized chair pictograms around
 * a table card. Positions come from lib/tableChairs.ts. Colors inherit
 * from the parent via `currentColor`, so the same Tailwind text-color
 * class that tints the table card also tints its chairs.
 *
 * Purely presentational. No state, no events. Pointer-events disabled so
 * chairs never intercept clicks meant for the table card.
 */
import { CHAIR_PAD, type ChairPosition } from '@/lib/tableChairs'

interface TableSeatRingProps {
  /** Chair positions in table-local coordinates (top-left origin). */
  positions: ChairPosition[]
  /** Visual size of each chair pictogram in px. */
  chairSize?: number
  /** Table card dimensions; defines the SVG viewport. */
  tableWidth: number
  tableHeight: number
}

const DEFAULT_CHAIR_SIZE = 18

/**
 * The chair pictogram is two stacked rounded rectangles: the chair back
 * (taller, thinner, on top) and the seat (wider, shorter, below). Rendered
 * around (0,0); rotated and translated by the parent <g>.
 */
function Chair({ size }: { size: number }) {
  const seatW = size * 0.92
  const seatH = size * 0.36
  const backW = size * 0.76
  const backH = size * 0.5
  // Back sits above the seat; small gap between them.
  const backY = -size / 2
  const seatY = backY + backH - 1
  return (
    <g>
      <rect
        x={-backW / 2}
        y={backY}
        width={backW}
        height={backH}
        rx={Math.max(2, size * 0.16)}
        fill="currentColor"
        opacity={0.82}
      />
      <rect
        x={-seatW / 2}
        y={seatY}
        width={seatW}
        height={seatH}
        rx={Math.max(2, size * 0.18)}
        fill="currentColor"
      />
    </g>
  )
}

export function TableSeatRing({
  positions,
  chairSize = DEFAULT_CHAIR_SIZE,
  tableWidth,
  tableHeight,
}: TableSeatRingProps) {
  // The SVG viewport extends past the table on all sides so chairs along
  // the edges don't get clipped. Origin is shifted by CHAIR_PAD so we
  // can keep using the table's local coordinates (0,0 = card top-left).
  const padded = CHAIR_PAD
  const vbX = -padded
  const vbY = -padded
  const vbW = tableWidth + padded * 2
  const vbH = tableHeight + padded * 2

  return (
    <svg
      className="pointer-events-none absolute"
      style={{
        left: -padded,
        top: -padded,
        width: vbW,
        height: vbH,
        // Make sure the chairs paint behind the card visually — the card's
        // own background/border sits above. Negative z handled by render
        // order in the parent (we mount this BEFORE the card).
        overflow: 'visible',
      }}
      viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
      aria-hidden="true"
    >
      {positions.map((p, idx) => (
        <g
          key={idx}
          transform={`translate(${p.x} ${p.y}) rotate(${p.angle})`}
        >
          <Chair size={chairSize} />
        </g>
      ))}
    </svg>
  )
}
